import { UnreachableCaseError } from "client_legacy/ndaw/errors";
import { ClientValidationErrorModel } from "client/Api/common";
import { documentsConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { SharePointEvent, publish } from "client_legacy/Documents/SharePoint/documentEvents";
import {
	DocumentItemViewModel,
	DocumentItemType,
	DocumentsErrorModel,
	FileViewModel
} from "client/Api/Documents/SharePoint/common";
import { convertToLocationModel } from "client_legacy/Documents/SharePoint/listLocationModelProvider";
import { EnabledView } from "client_legacy/Documents/SharePoint/DocumentsViewsPane/types";
import { copyFile } from "client/Api/Documents/SharePoint/sharePointAction";
import {
	copy,
	CopyAcrossDataStoresModel,
	SharePointCrossStoreItemModel
} from "client/Api/Documents/CrossStoreOperations/crossStoreActions";
import {
	ThingsToCopy,
	SharePointThingsToCopy,
	Destination,
	CopyNameConfiguration,
	convertToCrossStoreDestination,
	copyNotConfiguredMessage,
	SharePointDestination,
} from "./common";
import { CopyResult, DocumentStoreType } from "client/Api/Documents/common";
import { HttpRequestError } from "client/Api/httpClient";
import { isCancelledError } from "client_legacy/ndaw/cancellation";

class SharePointCopyImplementer {

	public computeNameConfigurationSettings(thingsToCopy: SharePointThingsToCopy): CopyNameConfiguration {
		if (thingsToCopy.documents.length !== 1) {
			return { shouldShow: false };
		}

		const { itemType, name } = thingsToCopy.documents[0];
		return {
			shouldShow: true,
			isFolder: itemType === DocumentItemType.Folder,
			supportsExtension: itemType === DocumentItemType.File,
			name
		};
	}

	public getEnabledView(
		thingsToCopy: ThingsToCopy
	): EnabledView {
		const { allowCrossStoreOperations } = documentsConfiguration();

		switch (thingsToCopy.storeKind) {
			case DocumentStoreType.SharePoint:
				if (allowCrossStoreOperations) {
					return {
						kind: "all"
					};
				}

				if (this.canBeCopiedDirectly(thingsToCopy.documents)) {
					return {
						kind: "all"
					};
				}

				return {
					kind: "none",
					reason: copyNotConfiguredMessage
				};
			case DocumentStoreType.WebDav:
				if (allowCrossStoreOperations) {
					return {
						kind: "all"
					};
				}
				return {
					kind: "none",
					reason: copyNotConfiguredMessage
				};
			default:
				throw new UnreachableCaseError(thingsToCopy);
		}
	}

	public copy(
		thingsToCopy: SharePointThingsToCopy,
		destination: Destination,
		name: string | null,
		signal: AbortSignal
	): Promise<CopyResult> {
		const { allowCrossStoreOperations } = documentsConfiguration();
		switch (destination.storeKind) {
			case DocumentStoreType.SharePoint:
				const documents: readonly DocumentItemViewModel[] = thingsToCopy.documents;
				if (this.canBeCopiedDirectly(documents)) {
					return this.copyDirectly(
						documents,
						destination,
						name,
						signal
					);
				}

				if (allowCrossStoreOperations) {
					return this.copyInTheBackground(
						thingsToCopy,
						destination,
						name,
						signal
					);
				}

				throw new Error("No Copy operation is available for the things which need to be copied");
			case DocumentStoreType.WebDav:
				if (allowCrossStoreOperations) {
					return this.copyInTheBackground(
						thingsToCopy,
						destination,
						name,
						signal
					);
				}

				throw new Error("Copy to WebDav is not configured");
			default:
				throw new UnreachableCaseError(destination);
		}
	}

	private canBeCopiedDirectly(documents: readonly DocumentItemViewModel[]): boolean {
		return documents.length === 1
			&& documents[0].itemType === DocumentItemType.File;
	}

	private async copyDirectly(
		documents: readonly DocumentItemViewModel[],
		destination: SharePointDestination,
		name: string | null,
		signal: AbortSignal
	): Promise<CopyResult> {
		const fileToCopy = documents[0] as FileViewModel;

		try {
			const { target } = destination;

			const document: FileViewModel = await copyFile(
				{
					sourceLocation: convertToLocationModel(fileToCopy.location),
					sourceFileId: fileToCopy.id,
					destinationLocation: convertToLocationModel(target.location),
					destinationFolderId: target.folderId,
					newFileName: name
				},
				signal
			);

			publish(SharePointEvent.AddedOrReplaced, document);

			return { kind: "success" };

		} catch (error) {
			if (isCancelledError(error)) {
				throw error;
			}

			const validationErrors: ClientValidationErrorModel[] = [];
			if (error instanceof HttpRequestError && error.statusCode === 400) {
				const errorModel: DocumentsErrorModel = error.readJson<DocumentsErrorModel>();

				if ("message" in errorModel) {
					validationErrors.push({
						propertyName: errorModel.field,
						errorMessage: errorModel.message
					});
				}
			}

			return { kind: "failed", errors: validationErrors };
		}
	}

	private async copyInTheBackground(
		thingsToCopy: SharePointThingsToCopy,
		destination: Destination,
		name: string | null,
		signal: AbortSignal
	): Promise<CopyResult> {
		const documents: readonly DocumentItemViewModel[] = thingsToCopy.documents;

		const itemsToCopy: SharePointCrossStoreItemModel[] = documents.map(it => ({
			id: it.id,
			location: convertToLocationModel(it.location)
		}));

		const data: CopyAcrossDataStoresModel = {
			items: {
				storeType: DocumentStoreType.SharePoint,
				sharePointItems: itemsToCopy
			},
			destination: convertToCrossStoreDestination(destination),
			newName: name
		};
		try {
			await copy(data, signal);
			return { kind: "scheduled" };

		} catch (error) {
			if (isCancelledError(error)) {
				throw error;
			}

			const validationErrors: ClientValidationErrorModel[] = [];
			if (error instanceof HttpRequestError && error.statusCode === 400) {
				const responseErrors = error.readJson<ClientValidationErrorModel[]>();
				validationErrors.push(...responseErrors);
			}
			return { kind: "failed", errors: validationErrors };
		}
	}
}

export {
	SharePointCopyImplementer
};
